import { Controller } from "@hotwired/stimulus"

// Controller for individual content block interactions
// Handles editing, regenerating, and updating blocks
export default class extends Controller {
  static targets = [
    "display", "editor", "editButton", "editButtonText", "regenerateButton",
    "editForm", "contentEditor", "saveButton", "loading", "error", "errorMessage",
    "controls", "contextMenu"
  ]
  
  static values = {
    blockId: String,
    draftId: String,
    contentAssetId: String,
    editable: Boolean
  }
  
  connect() {
    console.log("ContentBlockController connected for block:", this.blockIdValue)
    
    // Close context menu when clicking outside
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundHandleClickOutside)
    
    // Close context menu on escape key
    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.boundHandleEscape)
  }
  
  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside)
    document.removeEventListener("keydown", this.boundHandleEscape)
  }
  
  // Show context menu on right-click
  showContextMenu(event) {
    if (!this.editableValue) return
    
    event.preventDefault()
    
    const menu = this.contextMenuTarget
    if (!menu) return
    
    // Get position relative to the block element
    const blockRect = this.element.getBoundingClientRect()
    const x = event.clientX - blockRect.left
    const y = event.clientY - blockRect.top
    
    menu.style.left = `${x}px`
    menu.style.top = `${y}px`
    menu.classList.remove("hidden")
    
    // Ensure menu stays within viewport (adjust if needed)
    setTimeout(() => {
      const menuRect = menu.getBoundingClientRect()
      const blockRect = this.element.getBoundingClientRect()
      
      // Check if menu goes outside viewport
      if (menuRect.right > window.innerWidth) {
        menu.style.left = `${blockRect.width - menuRect.width - 10}px`
      }
      if (menuRect.bottom > window.innerHeight) {
        menu.style.top = `${blockRect.height - menuRect.height - 10}px`
      }
      if (menuRect.left < blockRect.left) {
        menu.style.left = `10px`
      }
      if (menuRect.top < blockRect.top) {
        menu.style.top = `10px`
      }
    }, 0)
  }
  
  // Handle clicks within context menu
  handleContextMenuClick(event) {
    // Let the button's click handler run
    event.stopPropagation()
  }
  
  // Close context menu when clicking outside
  handleClickOutside(event) {
    if (this.hasContextMenuTarget && !this.contextMenuTarget.contains(event.target) && !this.element.contains(event.target)) {
      this.hideContextMenu()
    }
  }
  
  // Close context menu on escape key
  handleEscape(event) {
    if (event.key === "Escape" && this.hasContextMenuTarget && !this.contextMenuTarget.classList.contains("hidden")) {
      this.hideContextMenu()
    }
  }
  
  // Hide context menu
  hideContextMenu() {
    if (this.hasContextMenuTarget) {
      this.contextMenuTarget.classList.add("hidden")
    }
  }
  
  // Toggle edit mode for the block
  toggleEdit() {
    // Hide context menu if open
    this.hideContextMenu()
    
    if (this.editorTarget.classList.contains("hidden")) {
      // Show editor, hide display
      this.displayTarget.classList.add("hidden")
      this.editorTarget.classList.remove("hidden")
      if (this.hasEditButtonTextTarget) {
        this.editButtonTextTarget.textContent = "Cancel"
      }
      
      // Initialize Lexxy editor if not already initialized
      this.initializeEditor()
    } else {
      // Hide editor, show display
      this.cancelEdit()
    }
  }
  
  // Cancel editing and return to display mode
  cancelEdit() {
    this.displayTarget.classList.remove("hidden")
    this.editorTarget.classList.add("hidden")
    if (this.hasEditButtonTextTarget) {
      this.editButtonTextTarget.textContent = "Edit"
    }
    this.hideError()
  }
  
  // Open regenerate modal
  openRegenerateModal() {
    // Hide context menu if open
    this.hideContextMenu()
    
    console.log("ContentBlockController: Opening regenerate modal for block:", this.blockIdValue)
    
    // Dispatch event to open modal (handled by regenerate modal controller)
    // Event bubbles to document level so modal controller can catch it
    const event = new CustomEvent("content-block:regenerate", {
      detail: {
        blockId: this.blockIdValue,
        draftId: this.draftIdValue,
        contentAssetId: this.contentAssetIdValue
      },
      bubbles: true,
      cancelable: true
    })
    
    // Dispatch from element (will bubble to document)
    this.element.dispatchEvent(event)
    console.log("ContentBlockController: Event dispatched, bubbles:", event.bubbles)
  }
  
  // Handle form submission for block updates
  async updateBlock(event) {
    event.preventDefault()
    
    this.showLoading()
    this.hideError()
    
    const form = event.target
    
    // Find the Lexxy editor element (lexxy-editor custom element)
    const lexxyEditor = form.querySelector('lexxy-editor')
    
    // Ensure Lexxy editor content is synced to form before submission
    if (lexxyEditor && lexxyEditor.editor) {
      // Lexxy uses formAssociated = true, so it should auto-sync
      // But we can manually trigger sync by getting the HTML
      try {
        const html = lexxyEditor.value || lexxyEditor.getHTML?.() || ""
        
        // Find the hidden input that ActionText creates
        const hiddenInput = form.querySelector('input[type="hidden"][name*="[content]"]')
        if (hiddenInput && html) {
          hiddenInput.value = html
          console.log("Synced Lexxy content to hidden input:", html.substring(0, 100))
        }
      } catch (e) {
        console.warn("Could not sync Lexxy content:", e)
      }
    }
    
    // Build FormData after syncing content
    const formData = new FormData(form)
    
    try {
      const response = await fetch(form.action, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: formData
      })
      
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          const errorText = await response.text()
          console.error("Error response text:", errorText)
        }
        throw new Error(errorMessage)
      }
      
      const data = await response.json()
      
      if (data.success) {
        // Reload page to ensure consistency and show updated content
        this.showSuccess("Block updated successfully")
        
        // Reload after a short delay to show success message
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showError(data.error || "Failed to update block")
        this.hideLoading()
      }
    } catch (error) {
      console.error("Error updating block:", error)
      this.showError(`An error occurred while updating the block: ${error.message}`)
      this.hideLoading()
    }
  }
  
  // Initialize Lexxy editor for the block
  initializeEditor() {
    const editorElement = this.contentEditorTarget
    if (!editorElement || editorElement.editor) {
      return // Already initialized
    }
    
    // Wait for Lexxy to be available
    if (typeof Lexxy === "undefined") {
      console.warn("Lexxy editor not available")
      return
    }
    
    // Initialize Lexxy editor
    try {
      // Lexxy should auto-initialize on rich_text_area fields
      // If not, we may need to manually initialize
      console.log("Lexxy editor should auto-initialize for:", editorElement.id)
    } catch (error) {
      console.error("Error initializing Lexxy editor:", error)
    }
  }
  
  // Show loading state
  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.saveButtonTarget.disabled = true
  }
  
  // Hide loading state
  hideLoading() {
    this.loadingTarget.classList.add("hidden")
    this.saveButtonTarget.disabled = false
  }
  
  // Show error message
  showError(message) {
    this.errorTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = message
  }
  
  // Hide error message
  hideError() {
    this.errorTarget.classList.add("hidden")
  }
  
  // Show success message (temporary)
  showSuccess(message) {
    // Create temporary success message
    const successDiv = document.createElement("div")
    successDiv.className = "mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
    successDiv.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
        <span class="text-sm text-green-700">${message}</span>
      </div>
    `
    this.element.insertBefore(successDiv, this.element.firstChild)
    
    // Remove after 3 seconds
    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }
}
